import Thread from "./Thread";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import useUser from "../../users/hooks/useUser";
import {
  DoctorsId,
  MessagesId,
  MsgType,
  SecretariesId,
  ThreadsId,
} from "cadendar-shared";
import { DateTime } from "luxon";
import ArrowLeft01Icon from "../../main/icons/arrow-left-01-stroke-rounded.tsx";
import ArrowRight01Icon from "../../main/icons/arrow-right-01-stroke-rounded.tsx";
import formatThreadsForDisplay from "../filterTransform/formatThreadsForDisplay";
import useAllUsers from "../../users/hooks/useAllUsers";
import useOwner from "../../users/hooks/useOwner";
import { trpc } from "../../main/components/trpc.ts";
import beep from "../../../utils/beep.ts";
import styles from "./MessagesMainComp.module.css";
import {Button} from "@/components/ui/button";
import MsgTextArea from "./MsgTextArea.tsx";
import RespondReplayModal from "./RespondReplayModal.tsx";
import useSelectedPatients from "../../patients/hooks/useSelectedPatients.ts";
import _ from "underts";
import { skipToken } from "@tanstack/react-query";
import { useSelectedPatientIds } from "../../main/state/globalState.ts";
import { messagesToThreadsPipeline } from "../filterTransform/messagesToThreadPipeline.ts";

const threadHasMsgNotSeen = (
  thread: {
    seen_by: string[];
    author_doctor_id: DoctorsId | null;
    author_secretary_id: SecretariesId | null;
  }[],
  user: { id: string }
) =>
  thread.some(
    (obj) =>
      obj.author_doctor_id !== user.id &&
      obj.author_secretary_id !== user.id &&
      !obj.seen_by.includes(user.id)
  );

const threadHasEmergencyMsgNotSeen = (
  thread: {
    seen_by: string[];
    author_doctor_id: DoctorsId | null;
    author_secretary_id: SecretariesId | null;
    msg_type: MsgType;
  }[],
  user: { id: string }
) =>
  thread.some(
    (obj) =>
      obj.author_doctor_id !== user.id &&
      obj.author_secretary_id !== user.id &&
      !obj.seen_by.includes(user.id) &&
      obj.msg_type === "urgent"
  );

const threadLastMessageDate = (
  thread: {
    timestamp: Date;
  }[]
) => {
  return thread.reduce((acc, obj) => {
    return obj.timestamp.getTime() > acc.getTime() ? obj.timestamp : acc;
  }, new Date(0));
};

function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef(callback);

  // Remember the latest callback if it changes.
  useLayoutEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    // Don't schedule if no delay is specified.
    // Note: 0 is a valid value for delay.
    if (!delay && delay !== 0) {
      return;
    }

    const id = setInterval(() => savedCallback.current(), delay);

    return () => clearInterval(id);
  }, [delay]);
}

function markMessageSeen<T extends { seen_by: string[] }>(
  msg: T,
  userId: string
) {
  const seen_by = new Set(msg.seen_by);
  seen_by.add(userId);
  return { ...msg, seen_by: Array.from(seen_by) };
}

const MessagesMainComp = () => {
  const [respondingThreadId, setRespondingThreadId] =
    React.useState<ThreadsId | null>(null);
  const [respondReplayMessageId, setRespondReplayMessageId] =
    React.useState<MessagesId | null>(null);
  const [replyText, setReplyText] = useState<string>("");
  const [newThreadText, setNewThreadText] = useState("");
  const [msgReplyType, setMsgReplyType] = useState<MsgType>("info");
  const [newThreadType, setNewThreadType] = useState<
    "info" | "urgent" | "question" | "call"
  >("info");
  const [msgPatientId, setMsgPatientId] = useState<string | null>(null);
  const [msgPatientName, setMsgPatientName] = useState<string | null>(null);
  const [msgPatientForename, setMsgPatientForename] = useState<string | null>(
    null
  );

  const getWeekStart = (date: Date) => {
    return DateTime.fromJSDate(date)
      .startOf("day")
      .minus({ days: DateTime.fromJSDate(date).weekday - 1 })
      .toJSDate();
  };

  const [selectedWeek, setSelectedWeek] = useState<Date>(getWeekStart(new Date()));

  // Calculer startDate et endDate basés sur la semaine sélectionnée
  const startDate = selectedWeek;
  const endDate = DateTime.fromJSDate(selectedWeek)
    .plus({ days: 7 })
    .toJSDate();
  const user = useUser();
  const owner = useOwner();
  const isDoctor = user ? user.tag === "doctor" : false;

  const selectedPatient = _.first(useSelectedPatients());

  const [_unused, { selectPatientId }] = useSelectedPatientIds();

  const trpcutils = trpc.useUtils();
  trpc.subscriptions.onChange.useSubscription(
    { owner: owner as string },
    {
      enabled: !!owner && !!user?.id,
      onData: (data) => {
        if (!data) return;
        if (typeof data !== "object") {
          return;
        }

        if ("collection" in data && data.collection === "messages") {
          trpcutils.message.invalidate();
        }

        if ("collection" in data && data.collection === "rdvs") {
          trpcutils.rdv.invalidate();
        }
      },
    }
  );
  const messages =
    trpc.message.getMessages.useQuery(
      owner ? { startDate, endDate } : skipToken
    ).data || [];
  // const patient_idS = _.compact(messages.map((msg) => msg.patient_id));

  // const patients =
  //   trpc.patient.getByIds.useQuery({ _ids: patient_idS }).data || [];

  const allUsers = useAllUsers();

  // const messagesWithPatientsNames = messages.map((msg) => {
  //   const patient = patients.find((p) => p._id === msg.patient_id);
  //   return {
  //     ...msg,
  //     patient_name: patient
  //       ? `${patient.nom.toLocaleUpperCase()} ${formaters.capitaliseEveryFirstLetter(
  //           patient.prenom
  //         )}`
  //       : null,
  //   };
  // });
  // const grouped = _.groupByKey(messagesWithPatientsNames, "thread_id");

  // const threads = Object.values(grouped);
  const threads =
    (user && messagesToThreadsPipeline(messages, user, allUsers)) || [];
  const hasUnseenMessages =
    threads && !!user && threads.some((t) => threadHasMsgNotSeen(t, user));
  const hasUnseenEmergencies =
    threads &&
    user &&
    threads.some((t) => threadHasEmergencyMsgNotSeen(t, user));

  useEffect(() => {
    if (hasUnseenEmergencies) {
      beep.doublePlay();
    }
    if (hasUnseenMessages && !hasUnseenEmergencies) {
      beep.play();
    }
  }, [hasUnseenEmergencies, hasUnseenMessages]);

  useInterval(
    () => beep.play,
    hasUnseenMessages && !hasUnseenEmergencies ? 15 * 60 * 1000 : null
  );
  useInterval(
    () => beep.doublePlay(),
    hasUnseenEmergencies ? 5 * 60 * 1000 : null
  );
  // useEffect(() => {
  //   beep.play();
  // }, []);
  // useInterval(
  //   () => {
  //     beep.play();
  //   },
  //   threads && user && threads.some((t) => threadHasMsgNotSeen(t, user))
  //     ? 15 * 60 * 1000
  //     : null
  // );
  // useInterval(
  //   () => {
  //     beep.doublePlay();
  //   },
  //   threads &&
  //     user &&
  //     threads.some((t) => threadHasEmergencyMsgNotSeen(t, user))
  //     ? 5 * 60 * 1000
  //     : null
  // );

  const convertedThreads =
    threads && user
      ? threads.map((thread) =>
          formatThreadsForDisplay(thread, new Date(), allUsers, user.id)
        )
      : [];
  const sortedThreads = convertedThreads.sort((a, b) => {
    return threadLastMessageDate(a).getTime() >
      threadLastMessageDate(b).getTime()
      ? -1
      : 1;
  });

  const archiveThreadMutation = trpc.message.archiveThread.useMutation({
    onMutate: (threadId) => {
      trpcutils.message.getMessages.setData({ startDate, endDate }, (state) => {
        return state!.filter((thread) => thread.thread_id !== threadId);
      });
    },
    onSettled: () => {
      trpcutils.message.invalidate();
    },
  });

  const onClearNewThread = () => {
    setNewThreadText("");
    setNewThreadType("info");
  };
  const markMsgSeenMutation = trpc.message.markMessageSeen.useMutation({
    onMutate: ({ messageId }) => {
      trpcutils.message.getMessages.setData({ startDate, endDate }, (state) =>
        state!.map((msg) =>
          msg.id === messageId ? markMessageSeen(msg, user!.id) : msg
        )
      );
    },
    onSuccess: () => {
      trpcutils.message.invalidate();
    },
    onError: () => {
      trpcutils.message.invalidate();
    },
  });

  const onMarkMsgSeen = (messageId: MessagesId) => {
    markMsgSeenMutation.mutate({ messageId });
  };

  const markThreadSeenMutation = trpc.message.markThreadSeen.useMutation({
    onMutate: (threadId: number) => {
      trpcutils.message.getMessages.setData({ startDate, endDate }, (state) =>
        state!.map((msg) =>
          msg.thread_id === threadId ? markMessageSeen(msg, user!.id) : msg
        )
      );
    },
    onSuccess: () => {
      trpcutils.message.invalidate();
    },
    onError: () => {
      trpcutils.message.invalidate();
    },
  });

  console.log("MessagesMainComp render");

  const onResetReply = () => {
    setReplyText("");
    setMsgReplyType("info");
    setRespondingThreadId(null);
    setMsgPatientId(null);
    setMsgPatientName(null);
    setMsgPatientForename(null);
  };
  const addMsgToThreadMutation = trpc.message.addMessageToThread.useMutation({
    onMutate: ({ threadId, text, msgType }) => {
      const replyBackup = text;
      trpcutils.message.getMessages.cancel();
      trpcutils.message.getMessages.setData(
        { startDate, endDate },
        (messages) => {
          return [
            ...messages!,
            {
              id: -1 as MessagesId,
              thread_id: threadId as ThreadsId,
              message: text,
              author_doctor_id: user!.tag === "doctor" ? user!.id : null,
              author_secretary_id: user!.tag === "secretary" ? user!.id : null,
              owner_id: owner!,
              timestamp: new Date(),
              seen_by: [],
              msg_type: msgType,
              patient_id: msgPatientId,
              name: msgPatientName,
              forename: msgPatientForename,
            },
          ];
        }
      );
      onResetReply();

      return {
        replyBackup,
      };
    },

    onSuccess: () => {
      trpcutils.message.invalidate();
    },
    onError: (_err, _params, context) => {
      context && setReplyText(context.replyBackup);
    },
  });
  const onAddReplyToThread = () => {
    if (!respondingThreadId) {
      throw new Error("no respondingThreadid while calling onAddReplyToThread");
    }
    addMsgToThreadMutation.mutate({
      threadId: respondingThreadId,
      text: replyText,
      msgType: msgReplyType,
      patient_id: msgPatientId,
    });
  };

  const newThreadMutation = trpc.message.createNewThread.useMutation({
    onMutate: () => {
      onClearNewThread();
      const previousText = newThreadText;
      trpcutils.message.getMessages.setData(
        { startDate, endDate },
        (messages) => [
          ...(messages || []),
          {
            id: -1 as MessagesId,
            thread_id: -1 as ThreadsId,
            message: newThreadText,
            author_doctor_id: user!.tag === "doctor" ? user!.id : null,
            author_secretary_id: user!.tag === "secretary" ? user!.id : null,
            owner_id: owner!,
            seen_by: [],
            msg_type: newThreadType,
            timestamp: new Date(),
            patient_id: msgPatientId,
            name: msgPatientName,
            forename: msgPatientForename,
          },
        ]
      );
      return {
        previousText,
      };
    },
    onError: (_err, _ignored, context) => {
      context && setNewThreadText(context.previousText);
    },
    onSettled: () => {
      trpcutils.message.invalidate();
    },
  });
  const onNewThreadCommit = () => {
    newThreadMutation.mutate({
      text: newThreadText,
      msgType: newThreadType,
      patient_id: msgPatientId,
    });
  };

  const onReplyClick = (threadId: ThreadsId) => {
    setRespondingThreadId(threadId);
    markThreadSeenMutation.mutate(threadId);
  };

  const onArchiveClick = (threadId: number) => {
    archiveThreadMutation.mutate(threadId);
  };
  // const onMarkMsgSeend = (threadId: string, msgNumber: number)=>{
  // markMsgSeenMutation.mutate({threadId, msgNumber});
  // }
  const onEmergencyReplyToggle = () =>
    setMsgReplyType((state) => {
      return state === "urgent" ? "info" : "urgent";
    });
  const onQuestionReplyToggle = () =>
    setMsgReplyType((state) => {
      return state === "question" ? "info" : "question";
    });
  const onCallReplyToggle = () =>
    setMsgReplyType((state) => {
      return state === "call" ? "info" : "call";
    });
  const onWaitingReplyToggle = () =>
    setMsgReplyType((state) => {
      return state === "waiting" ? "info" : "waiting";
    });
  const onDoneReplyToggle = () =>
    setMsgReplyType((state) => {
      return state === "done" ? "info" : "done";
    });

  const onNewThreadEmergencyToggle = () =>
    setNewThreadType((state) => (state === "urgent" ? "info" : "urgent"));
  const onNewThreadCallToggle = () =>
    setNewThreadType((state) => (state === "call" ? "info" : "call"));
  const onNewThreadQuestionToggle = () =>
    setNewThreadType((state) => (state === "question" ? "info" : "question"));

  const canReplayRespondAudio = (message: {
    message: string;
    author_doctor_id: DoctorsId | null;
    author_secretary_id: SecretariesId | null;
  }) => {
    if (message.author_doctor_id || message.author_secretary_id) {
      return false;
    }

    return (
      message.message.includes("nous a envoyé le message suivant :") ||
      message.message.includes("Un message a été reçu d'un numéro inconnu (")
    );
  };

  const threadsComps =
    threads && threads.length > 0 ? (
      sortedThreads.map((thread, idx) => {
        const thread_id = _.first(thread)?.thread_id || null;
        if (!thread_id) {
          throw new Error("no thread_id");
        }
        return (
          <div key={"thread" + idx} style={{ display: "flex" }}>
            <Thread
              messages={thread.map((message) => ({
                ...message,
                showRespondReplayButton: canReplayRespondAudio(message),
              }))}
              isDoctor={isDoctor}
              onArchiveClick={onArchiveClick}
              isResponding={respondingThreadId === _.first(thread)?.thread_id}
              threadId={thread_id}
              replyText={replyText}
              onReplyTextChange={setReplyText}
              onReplySend={onAddReplyToThread}
              onMessageClick={onMarkMsgSeen}
              onReplyClick={() => (thread_id ? onReplyClick(thread_id) : null)}
              onReplyCancel={onResetReply}
              onEmergencyClick={onEmergencyReplyToggle}
              onQuestionClick={onQuestionReplyToggle}
              onCallClick={onCallReplyToggle}
              onWaitingClick={onWaitingReplyToggle}
              onDoneClick={onDoneReplyToggle}
              msgReplyType={msgReplyType}
              name={selectedPatient?.name || ""}
              firstName={selectedPatient?.forename || ""}
              onSelectPatientForMessage={() => {
                setMsgPatientId(selectedPatient?.id || null);
                setMsgPatientName(selectedPatient?.name || null);
                setMsgPatientForename(selectedPatient?.forename || null);
              }}
              onSelectPatient={selectPatientId}
              onRespondReplayClick={setRespondReplayMessageId}
              showReplyLink={true}
            />
          </div>
        );
      })
    ) : (
      <div className={styles.emptyMessagesBox}>
        Ici apparaîtront les messages échangés entre les membres de l'équipe
      </div>
    );
  const handleNewThreadTextChange = setNewThreadText;

  // Fonctions de navigation semaine
  const onPrevWeek = () => {
    const newWeek = DateTime.fromJSDate(selectedWeek)
      .minus({ weeks: 1 })
      .toJSDate();
    setSelectedWeek(newWeek);
  };

  const onNextWeek = () => {
    const tomorrow = DateTime.now().plus({ days: 1 }).toJSDate();
    const nextWeek = DateTime.fromJSDate(selectedWeek)
      .plus({ weeks: 1 })
      .toJSDate();

    // Ne pas aller au-delà de demain (pour que tous les messages du jour soient reçus)
    if (nextWeek <= tomorrow) {
      setSelectedWeek(nextWeek);
    }
  };

  const onCurrentWeek = () => {
    setSelectedWeek(getWeekStart(new Date()));
  };

  // Formater l'affichage de la semaine
  const formatWeekDisplay = (weekStart: Date) => {
    const weekEnd = DateTime.fromJSDate(weekStart).plus({ days: 6 }).toJSDate();
    const startStr = DateTime.fromJSDate(weekStart)
      .setLocale("fr")
      .toFormat("dd/MM");
    const endStr = DateTime.fromJSDate(weekEnd)
      .setLocale("fr")
      .toFormat("dd/MM/yyyy");
    return `Semaine du ${startStr} au ${endStr}`;
  };

  // Vérifier si on peut aller à la semaine suivante
  const canGoNext = () => {
    const tomorrow = DateTime.now().plus({ days: 1 }).toJSDate();
    const nextWeek = DateTime.fromJSDate(selectedWeek)
      .plus({ weeks: 1 })
      .toJSDate();
    return nextWeek <= tomorrow;
  };

  return (
    <>
      <div className={styles.messagesBox}>
        {threadsComps}

        {/* Sélecteur de semaine compact */}
        <div className={styles.weekSelector}>
          <div className={styles.weekSelectorRow}>
            <Button
              className={styles.weekIcon}
              onClick={onPrevWeek}
              id="button_prev_week_messages"
              size="icon"
              variant="ghost"
            >
              <ArrowLeft01Icon />
            </Button>
            <Button
              className={styles.weekDisplay}
              onClick={onCurrentWeek}
              id="button_current_week_messages"
              variant="ghost"
              size="sm"
            >
              {formatWeekDisplay(selectedWeek)}
            </Button>
            <Button
              className={`${styles.weekIcon} ${!canGoNext() ? styles.disabled : ""}`}
              onClick={canGoNext() ? onNextWeek : undefined}
              disabled={!canGoNext()}
              id="button_next_week_messages"
              size="icon"
              variant="ghost"
            >
              <ArrowRight01Icon />
            </Button>
          </div>
        </div>
      </div>
      <div className={styles.replyContainer}>
        <MsgTextArea
          id="post_message"
          className={styles.post_message}
          placeholder="Entrez un nouveau message"
          name={selectedPatient?.name || ""}
          firstName={selectedPatient?.forename || ""}
          onCancel={onClearNewThread}
          onSelect={() => {
            setMsgPatientId(selectedPatient?.id || null);
          }}
          onChange={handleNewThreadTextChange}
          onSubmit={onNewThreadCommit}
          value={newThreadText}
        />

        <div className={styles.flexContainer}>
          <label className={styles.label} htmlFor="checkbox_emergency">
            <input
              type="checkbox"
              id="checkbox_emergency"
              checked={newThreadType === "urgent"}
              onChange={onNewThreadEmergencyToggle}
              className={styles.cursorPointer}
            />
            Urgent
          </label>
          <label className={styles.label} htmlFor="checkbox_question">
            <input
              type="checkbox"
              id="checkbox_question"
              checked={newThreadType === "question"}
              onChange={onNewThreadQuestionToggle}
              className={styles.cursorPointer}
            />
            Question
          </label>
          <label className={styles.label} htmlFor="checkbox_call">
            <input
              type="checkbox"
              id="checkbox_call"
              checked={newThreadType === "call"}
              onChange={onNewThreadCallToggle}
            />
            A appeler
          </label>
          <Button
            onClick={onNewThreadCommit}
            id="button_post_message"
            size="sm"
            variant="secondary"
          >
            Envoi
          </Button>
        </div>
      </div>
      {owner ? (
        <RespondReplayModal
          isOpen={respondReplayMessageId !== null}
          ownerId={owner}
          messageId={respondReplayMessageId}
          onClose={() => setRespondReplayMessageId(null)}
        />
      ) : null}
    </>
  );
};

export default MessagesMainComp;
